import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';

import { Observable } from 'rxjs';

import {
  InsuranceClaimApiResponse,
  InsuranceClaimCreateRequest,
  InsuranceClaimFilters,
  InsuranceClaimQueryParams,
  InsuranceClaimStatistics,
  InsuranceClaimUpdateRequest,
} from '../../entities/insurance/insurance-claim-api.types';
import { InsuranceClaim } from '../../entities/insurance/insurance-claim.entities';
import { EntityCreatedResponse, PaginatedResponse } from '../../models/api/api-response.model';
import { BaseApiService } from '../api/base-api.service';

@Injectable({
  providedIn: 'root'
})
export class InsuranceClaimService extends BaseApiService {

  /**
   * Get all insurance claims with pagination and optional filters
   */
  getClaims(params?: InsuranceClaimQueryParams, filters?: InsuranceClaimFilters): Observable<PaginatedResponse<InsuranceClaim>> {
    // Get farm_id from filters - it's required for the path
    const farmId = filters?.farm_id;
    if (!farmId) {
      throw new Error('Farm ID is required to get insurance claims');
    }

    let httpParams = new HttpParams();

    // Add required search parameter (API requires 'q' parameter)
    if (params?.q && params.q.trim() !== '') {
      httpParams = httpParams.set('q', params.q.trim());
    }

    if (params) {
      if (params.skip !== undefined) {
        httpParams = httpParams.set('skip', params.skip.toString());
      }
      if (params.limit !== undefined) {
        httpParams = httpParams.set('limit', params.limit.toString());
      }
    }

    // Add other filters as query parameters (excluding farm_id which goes in path)
    if (filters) {
      if (filters.policy_id) {
        httpParams = httpParams.set('_policy_id', filters.policy_id);
      }
      if (filters.status) {
        httpParams = httpParams.set('_status', filters.status);
      }
      if (filters.claim_type) {
        httpParams = httpParams.set('_claim_type', filters.claim_type);
      }
    }

    const endpoint = `/farms/${farmId}/insurance/claims`;
    return this.getPaginated<InsuranceClaim>(endpoint, httpParams);
  }

  /**
   * Get insurance claim by ID
   */
  getClaimById(id: string, farmId: string): Observable<InsuranceClaimApiResponse> {
    return this.get<InsuranceClaimApiResponse>(`/farms/${farmId}/insurance/claims/${id}`);
  }

  /**
   * Create new insurance claim
   */
  createClaim(claim: InsuranceClaimCreateRequest, farmId: string): Observable<EntityCreatedResponse> {
    return this.post<EntityCreatedResponse>(`/farms/${farmId}/insurance/claims`, claim);
  }

  /**
   * Update insurance claim
   */
  updateClaim(id: string, updates: InsuranceClaimUpdateRequest, farmId: string): Observable<void> {
    return this.put<void>(`/farms/${farmId}/insurance/claims/${id}`, updates);
  }

  /**
   * Patch insurance claim (partial update)
   */
  patchClaim(id: string, updates: Partial<InsuranceClaimUpdateRequest>, farmId: string): Observable<void> {
    return this.patch<void>(`/farms/${farmId}/insurance/claims/${id}`, updates);
  }

  /**
   * Delete insurance claim
   */
  deleteClaim(id: string, farmId: string): Observable<void> {
    return this.delete<void>(`/farms/${farmId}/insurance/claims/${id}`);
  }

  /**
   * Get claims by policy ID
   */
  getClaimsByPolicy(policyId: string, params?: InsuranceClaimQueryParams): Observable<PaginatedResponse<InsuranceClaim>> {
    return this.getClaims(params, { policy_id: policyId });
  }

  /**
   * Get claims by farm ID
   */
  getClaimsByFarm(farmId: string, params?: InsuranceClaimQueryParams): Observable<PaginatedResponse<InsuranceClaim>> {
    return this.getClaims(params, { farm_id: farmId });
  }

  /**
   * Get claims by status
   */
  getClaimsByStatus(status: string, params?: InsuranceClaimQueryParams): Observable<PaginatedResponse<InsuranceClaim>> {
    return this.getClaims(params, { status });
  }

  /**
   * Get claims by type
   */
  getClaimsByType(claimType: string, params?: InsuranceClaimQueryParams): Observable<PaginatedResponse<InsuranceClaim>> {
    return this.getClaims(params, { claim_type: claimType });
  }

  /**
   * Get claim statistics for a farm
   */
  getClaimStatistics(farmId: string): Observable<InsuranceClaimStatistics> {
    return this.get<InsuranceClaimStatistics>(`/farms/${farmId}/insurance/statistics`);
  }
}
